/**
 * User game history — pulls all completed games a user participated in,
 * directly from Firestore. Used by:
 *   - StatsScreen: aggregate stats (wins, best, win-rate, current streak)
 *   - GameHistoryScreen: chronological list with W/L/D markers
 *   - AchievementsScreen: derives unlocks from totals
 *   - SoloSetupScreen: recent solo games (via fetchSoloHistory)
 *
 * All queries are gated on `participantIds array-contains <uid>` — bots
 * are excluded from the participant list at write time, so we never count
 * fake players.
 */

import { db } from './firebase';
import {
  collection, query, where, orderBy, limit as fsLimit, getDocs,
  type DocumentData, type Query, type QuerySnapshot,
} from 'firebase/firestore';

export interface UserGameRow {
  gameId: string;
  endedAtMs: number;
  mode: 'solo' | 'multiplayer';
  rounds: number;
  difficulty?: string | null;
  /** This user's score in that game. */
  yourScore: number;
  /** Top opponent score (max of others). */
  topOpponentScore: number;
  /** From this user's perspective. */
  outcome: 'win' | 'loss' | 'draw';
  winnerName: string | null;
  /** Other players (humans + bots) in display order — excludes the user. */
  opponents: Array<{ id: string; name: string; avatar: string; isBot: boolean; score: number }>;
}

export interface UserStats {
  total: number;
  wins: number;
  losses: number;
  draws: number;
  winRate: number; // 0..1
  bestScore: number;
  currentStreak: number; // consecutive wins from most recent
  longestStreak: number;
}

function deriveOutcome(uid: string, scores: Record<string, number>): { outcome: UserGameRow['outcome']; yourScore: number; topOpponentScore: number } {
  const yourScore = scores[uid] ?? 0;
  let topOpponentScore = 0;
  for (const [pid, s] of Object.entries(scores)) {
    if (pid === uid) continue;
    if (s > topOpponentScore) topOpponentScore = s;
  }
  const outcome: UserGameRow['outcome'] =
    yourScore > topOpponentScore ? 'win' :
    yourScore < topOpponentScore ? 'loss' : 'draw';
  return { outcome, yourScore, topOpponentScore };
}

/**
 * THE QUERY. One definition, used by the one-shot read and by the live
 * subscription, so the two can never drift into disagreeing about what "my
 * games" means. Index-covered (`firestore.indexes.json`: participantIds
 * array-contains + endedAt desc) and hard-bounded by `limit`.
 */
export function myGamesQuery(userId: string, max = 100): Query<DocumentData> | null {
  if (!db || !userId) return null;
  return query(
    collection(db, 'gameRecords'),
    where('participantIds', 'array-contains', userId),
    orderBy('endedAt', 'desc'),
    fsLimit(max),
  );
}

/** One gameRecord, from this user's point of view. */
function toUserGameRow(userId: string, docId: string, d: any): UserGameRow {
  const scores = d.scores || {};
  const { outcome, yourScore, topOpponentScore } = deriveOutcome(userId, scores);
  const opponents = (d.players || [])
    .filter((p: any) => p.id !== userId)
    .map((p: any) => ({
      id: p.id,
      name: p.name,
      avatar: p.avatar,
      isBot: !!p.isBot,
      score: scores[p.id] ?? 0,
    }));
  return {
    gameId: docId,
    endedAtMs: d.endedAt?.toMillis ? d.endedAt.toMillis() : (d.endedAtMs || Date.now()),
    mode: d.mode || 'multiplayer',
    rounds: d.rounds || (d.roundDetails?.length ?? 0),
    difficulty: d.difficulty || null,
    yourScore,
    topOpponentScore,
    outcome,
    winnerName: d.winnerName || null,
    opponents,
  };
}

/**
 * The live mapper. Curried on the uid because a game only has an outcome from
 * somebody's point of view. Safe to build inline in a component: `useLiveQuery`
 * holds the mapper in a ref and does not resubscribe when its identity changes.
 */
export const mapUserGames =
  (userId: string) =>
    (snap: QuerySnapshot<DocumentData>): UserGameRow[] =>
      snap.docs.map(d => toUserGameRow(userId, d.id, d.data()));

export async function fetchUserGames(userId: string, limit = 100): Promise<UserGameRow[]> {
  if (!db || !userId) return [];
  try {
    const q = myGamesQuery(userId, limit);
    if (!q) return [];
    const snap = await getDocs(q);
    const out: UserGameRow[] = [];
    snap.forEach(doc => out.push(toUserGameRow(userId, doc.id, doc.data())));
    return out;
  } catch (e) {
    console.warn('[userHistoryService] fetchUserGames failed:', (e as any)?.message || e);
    return [];
  }
}

/** Roll up a list of games into aggregate stats. Newest first ordering
 *  matters for streak counting. */
export function deriveStats(games: UserGameRow[]): UserStats {
  if (!games.length) {
    return { total: 0, wins: 0, losses: 0, draws: 0, winRate: 0, bestScore: 0, currentStreak: 0, longestStreak: 0 };
  }
  let wins = 0, losses = 0, draws = 0, bestScore = 0;
  for (const g of games) {
    if (g.outcome === 'win') wins++;
    else if (g.outcome === 'loss') losses++;
    else draws++;
    if (g.yourScore > bestScore) bestScore = g.yourScore;
  }
  const total = games.length;
  // Streak: consecutive wins from the most recent game backward.
  let currentStreak = 0;
  for (const g of games) {
    if (g.outcome === 'win') currentStreak++;
    else break;
  }
  // Longest streak: scan in chronological order.
  let longestStreak = 0;
  let run = 0;
  for (let i = games.length - 1; i >= 0; i--) {
    if (games[i].outcome === 'win') {
      run++;
      if (run > longestStreak) longestStreak = run;
    } else {
      run = 0;
    }
  }
  return {
    total,
    wins,
    losses,
    draws,
    winRate: total > 0 ? wins / total : 0,
    bestScore,
    currentStreak,
    longestStreak,
  };
}
